// src/hooks/useWebSocket.js
import { useEffect, useRef, useCallback, useState } from 'react';
import { getAuthData } from '../utils/auth';

// Use VITE environment variable to match Dockerfile
const WS_URL = import.meta.env.VITE_WS_URL || 'wss://ws.simpleclass.eu';

// Global WebSocket instance to prevent duplicates
let globalWsInstance = null;
let globalHandlers = new Map();
let globalIsConnecting = false;
let globalConnectionCount = 0;
let globalHandlerIdCounter = 0; // ADD THIS: Counter for unique handler IDs

export const useWebSocket = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const reconnectTimeout = useRef(null);
  const messageQueue = useRef([]);
  const mountedRef = useRef(true);
  
  // Reconnection settings
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000;

  // List of message types to suppress logging for
  const suppressedLogs = new Set([
    'session_status_change',
    'calendar_update',
    'connected',
    'pong',
    'heartbeat'
  ]);

  // Process queued messages
  const processQueuedMessages = useCallback(() => {
    if (globalWsInstance && globalWsInstance.readyState === WebSocket.OPEN && messageQueue.current.length > 0) {
      while (messageQueue.current.length > 0) {
        const message = messageQueue.current.shift();
        try {
          globalWsInstance.send(JSON.stringify(message));
          // Only log important messages
          if (!suppressedLogs.has(message.type) && !message.type?.includes('subscribe')) {
            console.log('📤 Sent:', message.type);
          }
        } catch (error) {
          console.error('Error sending queued message:', error);
          messageQueue.current.push(message);
          break;
        }
      }
    }
  }, []);

  // Send message through WebSocket
  const sendMessage = useCallback((message) => {
    const ws = globalWsInstance;
    
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify(message));
        // Only log important messages
        if (!suppressedLogs.has(message.type) && 
            !message.type?.includes('subscribe') && 
            !message.type?.includes('unsubscribe')) {
          console.log('📤 Sent:', message.type);
        }
        return true;
      } catch (error) {
        console.error('Error sending message:', error);
        messageQueue.current.push(message);
        return false;
      }
    } else {
      // Only log if it's an important message
      if (!suppressedLogs.has(message.type) && !message.type?.includes('subscribe')) {
        console.log('⏳ Queueing message:', message.type);
      }
      messageQueue.current.push(message);
      
      // Try to reconnect if not already connecting
      if (!globalIsConnecting && !globalWsInstance) {
        connect();
      }
      return false;
    }
  }, []);

  // Broadcast message to all connected clients
  const broadcastMessage = useCallback((type, data) => {
    const message = {
      ...data,
      type: type,
      timestamp: new Date().toISOString()
    };
    return sendMessage(message);
  }, [sendMessage]);

  // FIXED: Add a message handler with unique ID
  const addMessageHandler = useCallback((type, handler) => {
    if (!globalHandlers.has(type)) {
      globalHandlers.set(type, new Map());
    }
    
    // Generate unique handler ID
    const handlerId = ++globalHandlerIdCounter;
    const handlers = globalHandlers.get(type);
    
    // Store handler with its ID
    handlers.set(handlerId, {
      callback: handler,
      id: handlerId
    });
    
    // Only log non-suppressed handlers
    if (!suppressedLogs.has(type)) {
      console.log(`📝 Handler registered: ${type} (ID: ${handlerId})`);
    }
    
    // Return cleanup function that only removes this specific handler
    return () => {
      const handlers = globalHandlers.get(type);
      if (handlers && handlers.has(handlerId)) {
        handlers.delete(handlerId);
        
        // If no more handlers for this type, remove the type entry
        if (handlers.size === 0) {
          globalHandlers.delete(type);
        }
        
        // Only log non-suppressed handlers
        if (!suppressedLogs.has(type)) {
          console.log(`🗑️ Handler removed: ${type} (ID: ${handlerId})`);
        }
      }
    };
  }, []);

  // FIXED: Process incoming messages with updated handler structure
  const processMessage = useCallback((data) => {
    // Only log non-suppressed message types
    if (!suppressedLogs.has(data.type)) {
      console.log('📨 Received:', data.type);
    }

    // Handle force_logout immediately — another device has logged in with this account.
    // Fire a browser event so App.jsx can react regardless of component mount state.
    if (data.type === 'force_logout') {
      console.warn('🔐 force_logout received — another device logged in');
      window.dispatchEvent(new CustomEvent('simpleclass:force_logout', {
        detail: { reason: data.reason || 'other_device' }
      }));
      return;
    }

    // Handle different message types from server
    const messageType = data.type;
    
    // Get handlers for this message type
    const handlers = globalHandlers.get(messageType);
    if (handlers && handlers.size > 0) {
      handlers.forEach(handlerData => {
        try {
          // Call the callback function from the handler data
          handlerData.callback(data);
        } catch (error) {
          console.error(`Error in handler for ${messageType}:`, error);
        }
      });
    } else if (!suppressedLogs.has(messageType)) {
      // Only warn for non-suppressed types
      console.warn(`⚠️ No handlers for: ${messageType}`);
    }
    
    // Also check for wildcard handlers
    const wildcardHandlers = globalHandlers.get('*');
    if (wildcardHandlers && wildcardHandlers.size > 0) {
      wildcardHandlers.forEach(handlerData => {
        try {
          handlerData.callback({ ...data, originalType: messageType });
        } catch (error) {
          console.error('Error in wildcard handler:', error);
        }
      });
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    // Prevent multiple simultaneous connections
    if (globalIsConnecting || (globalWsInstance && globalWsInstance.readyState === WebSocket.OPEN)) {
      if (globalWsInstance && globalWsInstance.readyState === WebSocket.OPEN) {
        setIsConnected(true);
      }
      return;
    }

    // Use existing connection if connecting
    if (globalWsInstance && globalWsInstance.readyState === WebSocket.CONNECTING) {
      return;
    }

    const authData = getAuthData();
    
    if (!authData || !authData.token) {
      setConnectionError('Not authenticated');
      return;
    }

    globalIsConnecting = true;
    globalConnectionCount++;
    
    // Only log on first connection attempt
    if (globalConnectionCount === 1) {
      console.log('🚀 Initializing WebSocket connection...');
    }
    
    try {
      // Create WebSocket with auth token and institution ID
      const userId = localStorage.getItem('userId');
      const institutionId = localStorage.getItem('currentInstitutionId');

      // Browser instance ID — shared across all tabs in the same browser via
      // localStorage, but unique per browser/device. Used by the server to allow
      // multiple tabs while still kicking connections from other devices.
      let browserInstanceId = localStorage.getItem('simpleclass_browser_id');
      if (!browserInstanceId) {
        browserInstanceId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        localStorage.setItem('simpleclass_browser_id', browserInstanceId);
      }

      const wsUrl = `${WS_URL}?token=${encodeURIComponent(authData.token)}&userId=${userId || ''}&institutionId=${institutionId || ''}&browserId=${encodeURIComponent(browserInstanceId)}`;
      
      // Only log URL on first connection
      if (globalConnectionCount === 1) {
        console.log('🔌 Connecting to:', WS_URL);
      }
      
      const ws = new WebSocket(wsUrl);
      globalWsInstance = ws;
      
      ws.onopen = () => {
        console.log('✅ WebSocket connected');
        globalIsConnecting = false;
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttempts.current = 0;
        
        // Process any queued messages
        processQueuedMessages();
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Handle connection confirmation silently
          if (data.type === 'connected') {
            return;
          }
          
          // Process all other messages
          processMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
      
      ws.onerror = (error) => {
        console.error('❌ WebSocket error');
        globalIsConnecting = false;
        setConnectionError('WebSocket connection failed');
      };
      
      ws.onclose = (event) => {
        globalIsConnecting = false;
        globalWsInstance = null;
        setIsConnected(false);
        
        // Only log unexpected disconnections
        if (event.code !== 1000 && event.code !== 1001) {
          console.log('🔴 WebSocket disconnected. Code:', event.code);
        }
        
        // Handle specific close codes
        if (event.code === 4001) {
          // Token expired
          console.error('❌ Token expired');
          setConnectionError('Session expired');
          if (window.handleAuthError) {
            window.handleAuthError(true);
          }
          return;
        }
        
        // Attempt to reconnect if not a normal closure
        if (event.code !== 1000 && event.code !== 1001 && event.code !== 4000 && event.code !== 4001 && 
            reconnectAttempts.current < maxReconnectAttempts && mountedRef.current) {
          reconnectAttempts.current++;
          const delay = reconnectDelay * Math.pow(2, reconnectAttempts.current - 1);
          
          reconnectTimeout.current = setTimeout(() => {
            if (mountedRef.current) {
              connect();
            }
          }, delay);
        }
      };
      
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      globalIsConnecting = false;
      setConnectionError(error.message);
    }
  }, [processMessage, processQueuedMessages]);

  // Subscribe to session updates
  const subscribeToSession = useCallback((sessionId) => {
    sendMessage({
      type: 'subscribe_session_live',
      sessionId: sessionId
    });
  }, [sendMessage]);

  // Subscribe to room assignments
  const subscribeToRoomAssignments = useCallback((sessionId) => {
    sendMessage({
      type: 'subscribe_room_assignments',
      sessionId: sessionId
    });
  }, [sendMessage]);

  // Subscribe to session chat
  const subscribeToSessionChat = useCallback((sessionId) => {
    sendMessage({
      type: 'subscribe_session_chat',
      sessionId: sessionId
    });
  }, [sendMessage]);

  // Subscribe to calendar updates
  const subscribeToCalendar = useCallback((startDate, endDate) => {
    sendMessage({
      type: 'subscribe_calendar',
      startDate: startDate,
      endDate: endDate
    });
  }, [sendMessage]);

  // Subscribe to personal chat
  const subscribeToPersonalChat = useCallback(() => {
    sendMessage({
      type: 'subscribe_personal_chat'
    });
  }, [sendMessage]);

  // Subscribe to session list
  const subscribeToSessionList = useCallback((courseId) => {
    sendMessage({
      type: 'subscribe_session_list',
      courseId: courseId
    });
  }, [sendMessage]);

  // Send chat message
  const sendChatMessage = useCallback((data) => {
    sendMessage({
      type: 'send_chat_message',
      ...data
    });
  }, [sendMessage]);

  // Send typing indicator
  const sendTypingIndicator = useCallback((sessionId, isTyping) => {
    sendMessage({
      type: 'typing_indicator',
      sessionId: sessionId,
      isTyping: isTyping
    });
  }, [sendMessage]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }
    
    if (globalWsInstance) {
      globalWsInstance.close(1000, 'User disconnect');
      globalWsInstance = null;
    }
    
    setIsConnected(false);
    setConnectionError(null);
    reconnectAttempts.current = 0;
    messageQueue.current = [];
  }, []);

  // Initialize connection on mount
  useEffect(() => {
    mountedRef.current = true;
    
    // Only connect if not already connected
    if (!globalWsInstance || globalWsInstance.readyState !== WebSocket.OPEN) {
      const initTimer = setTimeout(() => {
        connect();
      }, 100);
      
      return () => {
        clearTimeout(initTimer);
        mountedRef.current = false;
        if (reconnectTimeout.current) {
          clearTimeout(reconnectTimeout.current);
        }
      };
    } else {
      setIsConnected(true);
    }
    
    return () => {
      mountedRef.current = false;
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
    };
  }, [connect]);

  // Monitor auth changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue && !globalWsInstance) {
          console.log('🔑 Token added, connecting...');
          connect();
        } else if (!e.newValue && globalWsInstance) {
          console.log('🔑 Token removed, disconnecting...');
          disconnect();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [connect, disconnect]);

  // Periodic auth check - reduced frequency
  useEffect(() => {
    const checkAuth = () => {
      const authData = getAuthData();
      const hasAuth = !!(authData && authData.token);
      
      if (hasAuth && !isConnected && !globalWsInstance) {
        connect();
      } else if (!hasAuth && (isConnected || globalWsInstance)) {
        disconnect();
      }
    };

    const interval = setInterval(checkAuth, 30000); // Check every 30 seconds instead of 10
    
    return () => clearInterval(interval);
  }, [connect, disconnect, isConnected]);

  return {
    isConnected,
    connectionError,
    sendMessage,
    broadcastMessage,
    addMessageHandler,
    connect,
    disconnect,
    // Subscription methods
    subscribeToSession,
    subscribeToRoomAssignments,
    subscribeToSessionChat,
    subscribeToCalendar,
    subscribeToPersonalChat,
    subscribeToSessionList,
    sendChatMessage,
    sendTypingIndicator
  };
};